import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Sidebar } from './components/Sidebar.tsx';
import { api, clearSession, hasSession, setUnauthorizedHandler, type AuthUser } from './lib/api.ts';
import { ChangePassword } from './routes/ChangePassword.tsx';
import { Login } from './routes/Login.tsx';

/**
 * 路由级代码分割：管理页与设置页不是每个人每天都进，不该挤在首屏 bundle 里。
 */
const Meetings = lazy(() => import('./routes/Meetings.tsx').then((m) => ({ default: m.Meetings })));
const Admin = lazy(() => import('./routes/Admin.tsx').then((m) => ({ default: m.Admin })));
const Account = lazy(() => import('./routes/Account.tsx').then((m) => ({ default: m.Account })));
const Directory = lazy(() =>
  import('./routes/Directory.tsx').then((m) => ({ default: m.Directory })),
);
const FeishuDirectory = lazy(() =>
  import('./routes/FeishuDirectory.tsx').then((m) => ({ default: m.FeishuDirectory })),
);
const SettingsRoutes = lazy(() =>
  import('./routes/settings/SettingsRouter.tsx').then((m) => ({ default: m.SettingsRoutes })),
);

/* 版本更新提示。不自动刷新 —— 打断正在进行的操作比跑旧版更糟，选择权给人。 */
function UpdateBanner({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <div className="update-banner">
      <span>系统已更新到新版本，当前页面还在旧版 —— 部分功能可能缺失。</span>
      <button type="button" onClick={() => window.location.reload()}>
        立即刷新
      </button>
    </div>
  );
}

export function App() {
  const [authed, setAuthed] = useState(hasSession());
  const [me, setMe] = useState<AuthUser | null>(null);
  const [needsPwChange, setNeedsPwChange] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [outdated, setOutdated] = useState(false);

  // 版本检测：SPA 长驻的旧 bundle 是发版的盲区。index.html 是 no-cache 的，
  // 轮询它对比 bundle 文件名，发现新版本就提示刷新（不自动刷新）。
  useEffect(() => {
    if (import.meta.env.DEV) return;
    const check = (): void => {
      void fetch('/', { cache: 'no-store' })
        .then((r) => r.text())
        .then((html) => {
          const latest = /assets\/index-([A-Za-z0-9_-]+)\.js/.exec(html)?.[1];
          const current = /index-([A-Za-z0-9_-]+)\.js/.exec(
            document.querySelector('script[src*="assets/index-"]')?.getAttribute('src') ?? '',
          )?.[1];
          if (latest !== undefined && current !== undefined && latest !== current) {
            setOutdated(true);
          }
        })
        .catch(() => {});
    };
    void check();
    const t = window.setInterval(check, 5 * 60 * 1000);
    return () => window.clearInterval(t);
  }, []);

  // 任何接口返回 401（令牌过期/被吊销/被停用）时，清会话回到登录页
  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearSession();
      setMe(null);
      setNeedsPwChange(false);
      setAuthed(false);
    });
  }, []);

  // me 是否已取到（失败也算「已取到」）。**权限路由渲染的前提**：
  // perms 来自 me，me 未到时路由表里没有 /settings 这些条件路由，
  // 兜底的 Navigate 会把刷新中的地址弹回首页 —— 表现为「刷新后菜单选择失效」。
  const [meLoaded, setMeLoaded] = useState(false);

  const loadMe = useCallback((): void => {
    api
      .me()
      .then((r) => {
        setMe(r.user);
        setNeedsPwChange(r.user?.mustChangePassword === true);
      })
      .catch(() => {
        // 401 已由统一回调处理
      })
      .finally(() => {
        setMeLoaded(true);
      });
  }, []);

  useEffect(() => {
    if (!authed) return;
    loadMe();
  }, [authed, loadMe]);

  const signOut = (): void => {
    void api.logout().catch(() => {});
    clearSession();
    setMe(null);
    setNeedsPwChange(false);
    setAuthed(false);
  };

  // 导航与页面的权限显隐。服务端仍是真正的门（接口挂 permission 点），
  // 这里只决定「看不看得见入口」
  const perms = me?.permissions ?? [];

  if (!authed) {
    return (
      <>
        <UpdateBanner show={outdated} />
        <Login
          onAuthed={() => {
            setMeLoaded(false);
            setAuthed(true);
          }}
        />
      </>
    );
  }

  if (needsPwChange) {
    return (
      <>
        <UpdateBanner show={outdated} />
        <ChangePassword
          username={me?.username ?? ''}
          onDone={() => {
            // 改完重取 me：must_change_password 已被服务端清零，正常进入系统
            loadMe();
          }}
        />
      </>
    );
  }

  // 权限未就绪：渲染路由表会把带权限条件的地址误弹回首页
  if (!meLoaded) {
    return (
      <>
        <UpdateBanner show={outdated} />
        <div className="empty" style={{ minHeight: '60vh' }}>
          读取中…
        </div>
      </>
    );
  }

  if (showPw) {
    return (
      <>
        <UpdateBanner show={outdated} />
        <ChangePassword
          heading="修改密码"
          submitLabel="确认修改"
          username={me?.username ?? ''}
          onDone={() => setShowPw(false)}
          onCancel={() => setShowPw(false)}
        />
      </>
    );
  }

  return (
    <div className="layout">
      <UpdateBanner show={outdated} />
      <Sidebar
        perms={perms}
        me={me}
        onSignOut={signOut}
        onPassword={() => setShowPw(true)}
      />
      <main className="main">
        <Suspense fallback={<div className="empty">读取中…</div>}>
          <Routes>
            <Route path="/" element={<Navigate to="/meetings" replace />} />
            <Route path="/meetings" element={<Meetings me={me} />} />
            {perms.includes('user:manage') ||
            perms.includes('role:manage') ||
            perms.includes('department:manage') ? (
              <Route path="/admin" element={<Admin me={me} />} />
            ) : null}
            {perms.includes('tencent:user:list') ? (
              <Route path="/directory" element={<Directory />} />
            ) : null}
            {perms.includes('feishu:user:list') ? (
              <Route path="/feishu-directory" element={<FeishuDirectory />} />
            ) : null}
            <Route path="/account" element={<Account />} />
            {perms.includes('config:manage') && (
              <Route path="/settings/*" element={<SettingsRoutes />} />
            )}
            <Route path="*" element={<Navigate to="/meetings" replace />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}
